<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import type { SaleTransaction } from '@/types/farmsales'
import transactionService from '@/api/farmtransactions'
import TransactionFormModal from '@/components/farm/TransactionFormModal.vue'
import ConfirmDeleteModal from '@/components/farm/ConfirmDeleteModal.vue'
import WelcomeBanner from '@/components/farm/supervisor/WelcomeBanner.vue'
import { jsPDF } from 'jspdf'
import autoTable from 'jspdf-autotable'
import { addPdfWatermark } from '@/utils/pdfWatermark'
import { useAuthStore } from '@/stores/auth'

const authStore = useAuthStore()
const transactions = ref<SaleTransaction[]>([])
const showFormModal = ref(false)
const showDeleteModal = ref(false)
const selectedTransaction = ref<SaleTransaction | null>(null)

// Pagination
const currentPage = ref(1)
const itemsPerPage = ref(10)

// Search and filters
const searchQuery = ref('')
const createdByFilter = ref('')
const createdByOptions = ref<string[]>([])
const farmOptions = ref<string[]>([])
const selectedFarmFilter = ref('')

// Load transactions
const loadTransactions = async () => {
  transactions.value = await transactionService.list()
  // Extract unique created_by names for filter
  const createdBySet = new Set(transactions.value.map((t) => t.created_by_name))
  createdByOptions.value = Array.from(createdBySet).filter(Boolean) as string[]

  // Extract unique farm names for filter
  const farmSet = new Set(transactions.value.map((t) => t.farm_name))
  farmOptions.value = Array.from(farmSet).filter(Boolean) as string[]
}

onMounted(() => {
  loadTransactions()
})

// Filtered transactions
const filteredTransactions = computed(() => {
  return transactions.value.filter((transaction) => {
    const matchesSearch =
      searchQuery.value === '' ||
      transaction.farm_name.toLowerCase().includes(searchQuery.value.toLowerCase())

    const matchesCreator =
      createdByFilter.value === '' || transaction.created_by_name === createdByFilter.value

    const matchesFarm =
      selectedFarmFilter.value === '' || transaction.farm_name === selectedFarmFilter.value

    return matchesSearch && matchesCreator && matchesFarm
  })
})

// Total amount for filtered transactions
const totalAmount = computed(() => {
  return filteredTransactions.value.reduce((sum, transaction) => {
    return sum + (parseFloat(transaction.total_amount.toString()) || 0)
  }, 0)
})

// Paginated transactions
const paginatedTransactions = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage.value
  const end = start + itemsPerPage.value
  return filteredTransactions.value.slice(start, end)
})

// Total pages
const totalPages = computed(() => {
  return Math.ceil(filteredTransactions.value.length / itemsPerPage.value)
})

// Modal open handlers
const openNewTransaction = () => {
  selectedTransaction.value = null
  showFormModal.value = true
}
const openEditTransaction = (transaction: SaleTransaction) => {
  selectedTransaction.value = transaction
  showFormModal.value = true
}
const openDeleteTransaction = (transaction: SaleTransaction) => {
  selectedTransaction.value = transaction
  showDeleteModal.value = true
}

// Save handler
const handleSave = async (formData: Partial<SaleTransaction>) => {
  if (selectedTransaction.value) {
    // update existing
    await transactionService.update(selectedTransaction.value.id, formData)
  } else {
    // create new
    await transactionService.create(formData)
  }
  showFormModal.value = true
  await loadTransactions()
}

// Delete handler
const handleDelete = async () => {
  if (selectedTransaction.value) {
    await transactionService.delete(selectedTransaction.value.id)
    showDeleteModal.value = false
    await loadTransactions()
  }
}

// Generate PDF for all transactions of selected farm
const generateFarmPDF = () => {
  if (!selectedFarmFilter.value) {
    alert('Please select a farm first')
    return
  }

  const farmTransactions = filteredTransactions.value.filter(
    (t) => t.farm_name === selectedFarmFilter.value,
  )

  if (farmTransactions.length === 0) {
    alert('No transactions found for the selected farm')
    return
  }

  // Calculate total amount for the farm
  const farmTotalAmount = farmTransactions.reduce((sum, transaction) => {
    return sum + (parseFloat(transaction.total_amount.toString()) || 0)
  }, 0)

  // Create new PDF document with a professional font
  const doc = new jsPDF()

  // Set font - using helvetica which is a standard PDF font
  doc.setFont('helvetica')

  // Add title
  doc.setFontSize(20)
  doc.setTextColor(40, 103, 50) // Green color for farm theme
  doc.text('Farm Management System', 105, 20, { align: 'center' })

  // Add farm details
  doc.setFontSize(14)
  doc.setTextColor(0, 0, 0) // Black color
  doc.text(`Farm: ${selectedFarmFilter.value}`, 20, 35)
  doc.text(`Owner: ${farmTransactions[0].owner_name}`, 20, 45)
  doc.text(`Total Transactions: ${farmTransactions.length}`, 20, 55)
  doc.text(`Total Amount: ${farmTotalAmount.toLocaleString()}`, 20, 65)

  // Prepare table data
  const tableData = farmTransactions.map((t, index) => [
    (index + 1).toString(),
    t.yield_label,
    t.quantity.toString(),
    t.total_amount.toString(), // Removed currency symbol
    t.buyer_name,
    t.payment_method,
    t.created_by_name,
    new Date(t.created_at).toLocaleDateString(),
  ])

  // Add transaction table - removed invalid properties
  doc.setFontSize(12)
  autoTable(doc, {
    startY: 75,
    head: [
      ['#', 'Crop', 'Quantity', 'Total Amount', 'Buyer', 'Payment Method', 'Created By', 'Date'],
    ],
    body: tableData,
    theme: 'grid',
    headStyles: {
      fillColor: [76, 175, 80], // Green header
      textColor: [0, 0, 255],
      fontStyle: 'bold',
    },
    styles: {
      fontSize: 10,
      cellPadding: 3,
      overflow: 'linebreak',
    },
    margin: { top: 75 },
  })

  // Add footer with page numbers
  const pageCount = doc.getNumberOfPages()
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i)
    doc.setFontSize(10)
    doc.text(
      `Page ${i} of ${pageCount}`,
      doc.internal.pageSize.width / 2,
      doc.internal.pageSize.height - 10,
      {
        align: 'center',
      },
    )
  }

  // Save the PDF
  addPdfWatermark(doc)
  doc.save(
    `Farm_Transactions_${selectedFarmFilter.value}_${new Date().toISOString().slice(0, 10)}.pdf`,
  )
}

// Reset filters
const resetFilters = () => {
  searchQuery.value = ''
  createdByFilter.value = ''
  selectedFarmFilter.value = ''
  currentPage.value = 1
}
</script>

<template>
  <div class="min-h-screen font-sans bg-gray-50">
    <!-- Main content -->
    <div
      class="p-4 md:p-6 transition-all duration-300"
    >
      <WelcomeBanner />

      <div class="bg-white rounded-lg shadow-md p-4 md:p-6 mt-4 md:mt-6">
        <div
          class="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6"
        >
          <h1 class="text-xl md:text-2xl font-bold text-gray-800">Sales Transactions</h1>

          <!-- New Transaction Button -->
          <button
            @click="openNewTransaction"
            class="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center transition-colors w-full md:w-auto justify-center"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-5 w-5 mr-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 6v6m0 0v6m0-6h6m-6 0H6"
              />
            </svg>
            New Sale
          </button>
        </div>

        <!-- Search and Filter Section -->
        <div class="bg-gray-100 p-4 rounded-lg mb-6">
          <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
            <!-- Search Input -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1"
                >Search by Farm Name</label
              >
              <div class="relative">
                <input
                  v-model="searchQuery"
                  type="text"
                  placeholder="Enter farm name..."
                  class="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-5 w-5 text-gray-400 absolute left-3 top-2.5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
            </div>

            <!-- Created By Filter -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1"
                >Filter by Created By</label
              >
              <select
                v-model="createdByFilter"
                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              >
                <option value="">All Users</option>
                <option v-for="user in createdByOptions" :key="user" :value="user">
                  {{ user }}
                </option>
              </select>
            </div>

            <!-- Farm Filter -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Filter by Farm</label>
              <select
                v-model="selectedFarmFilter"
                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              >
                <option value="">All Farms</option>
                <option v-for="farm in farmOptions" :key="farm" :value="farm">{{ farm }}</option>
              </select>
            </div>

            <!-- Action Buttons -->
            <div class="flex flex-col md:flex-row md:items-end space-y-2 md:space-y-0 md:space-x-2">
              <button
                @click="resetFilters"
                class="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 flex items-center justify-center transition-colors"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-5 w-5 mr-2"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
                Reset
              </button>

              <button
                @click="generateFarmPDF"
                :disabled="!selectedFarmFilter"
                class="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
                title="Download all transactions for selected farm"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-5 w-5 mr-2"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                <span class="hidden md:inline">Download Farm PDF</span>
                <span class="md:hidden">PDF</span>
              </button>
            </div>
          </div>
        </div>

        <!-- Info Banner when farm is selected -->
        <div
          v-if="selectedFarmFilter"
          class="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4 flex items-center"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-5 w-5 text-blue-500 mr-2"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span class="text-blue-700 text-sm md:text-base">
            Showing transactions for <strong>{{ selectedFarmFilter }}</strong
            >. Use the "Download Farm PDF" button to download all transactions for this farm.
          </span>
        </div>

        <!-- Table Container with horizontal scrolling -->
        <div class="overflow-x-auto rounded-lg shadow">
          <table class="min-w-full bg-white border border-gray-200">
            <thead class="bg-green-700 text-white">
              <tr>
                <th
                  class="px-3 py-2 md:px-4 md:py-3 text-left text-xs font-medium uppercase tracking-wider"
                >
                  #
                </th>
                <th
                  class="px-3 py-2 md:px-4 md:py-3 text-left text-xs font-medium uppercase tracking-wider"
                >
                  Farm
                </th>
                <th
                  class="px-3 py-2 md:px-4 md:py-3 text-left text-xs font-medium uppercase tracking-wider"
                >
                  Crop
                </th>
                <th
                  class="px-3 py-2 md:px-4 md:py-3 text-left text-xs font-medium uppercase tracking-wider"
                >
                  Qty
                </th>
                <th
                  class="px-3 py-2 md:px-4 md:py-3 text-left text-xs font-medium uppercase tracking-wider"
                >
                  Total
                </th>
                <th
                  class="px-3 py-2 md:px-4 md:py-3 text-left text-xs font-medium uppercase tracking-wider"
                >
                  Buyer
                </th>
                <th
                  class="px-3 py-2 md:px-4 md:py-3 text-left text-xs font-medium uppercase tracking-wider"
                >
                  Payment
                </th>
                <th
                  class="px-3 py-2 md:px-4 md:py-3 text-left text-xs font-medium uppercase tracking-wider"
                >
                  Created By
                </th>
                <th
                  class="hidden md:table-cell px-4 py-3 text-left text-xs font-medium uppercase tracking-wider"
                >
                  Owner
                </th>
                <th
                  class="hidden lg:table-cell px-4 py-3 text-left text-xs font-medium uppercase tracking-wider"
                >
                  Created At
                </th>
                <th
                  class="hidden lg:table-cell px-4 py-3 text-left text-xs font-medium uppercase tracking-wider"
                >
                  Updated By
                </th>
                <th
                  class="px-3 py-2 md:px-4 md:py-3 text-left text-xs font-medium uppercase tracking-wider"
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-200">
              <tr v-for="(t, i) in paginatedTransactions" :key="t.id" class="hover:bg-gray-50">
                <td class="px-3 py-2 md:px-4 md:py-3 whitespace-nowrap">
                  {{ (currentPage - 1) * itemsPerPage + i + 1 }}
                </td>
                <td class="px-3 py-2 md:px-4 md:py-3 whitespace-nowrap">{{ t.farm_name }}</td>
                <td class="px-3 py-2 md:px-4 md:py-3 whitespace-nowrap">{{ t.yield_label }}</td>
                <td class="px-3 py-2 md:px-4 md:py-3 whitespace-nowrap">{{ t.quantity }}</td>
                <td class="px-3 py-2 md:px-4 md:py-3 whitespace-nowrap">{{ t.total_amount }}</td>
                <td class="px-3 py-2 md:px-4 md:py-3 whitespace-nowrap">{{ t.buyer_name }}</td>
                <td class="px-3 py-2 md:px-4 md:py-3 whitespace-nowrap">{{ t.payment_method }}</td>
                <td class="px-3 py-2 md:px-4 md:py-3 whitespace-nowrap">{{ t.created_by_name }}</td>
                <td class="hidden md:table-cell px-4 py-3 whitespace-nowrap">{{ t.owner_name }}</td>
                <td class="hidden lg:table-cell px-4 py-3 whitespace-nowrap">
                  {{ new Date(t.created_at).toLocaleString() }}
                </td>
                <td class="hidden md:table-cell px-4 py-3 whitespace-nowrap">
                  {{ t.updated_by_name }}
                </td>
                <td class="px-3 py-2 md:px-4 md:py-3 whitespace-nowrap">
                  <div class="flex space-x-2">
                    <button
                      @click="openEditTransaction(t)"
                      class="bg-blue-500 text-white p-1.5 rounded hover:bg-blue-600 transition-colors"
                      title="Edit"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="h-4 w-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                        />
                      </svg>
                    </button>
                    <button
                      v-if="authStore.user?.role === 'ADMIN'"
                      @click="openDeleteTransaction(t)"
                      class="bg-red-500 text-white p-1.5 rounded hover:bg-red-600 transition-colors"
                      title="Delete"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="h-4 w-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                    </button>
                    <button
                      v-else
                      disabled
                      class="bg-red-200 text-white p-1.5 rounded opacity-50 cursor-not-allowed"
                      title="Only admins can delete sales"
                    >
                      <span class="text-xs text-gray-500">View only</span>
                    </button>
                  </div>
                </td>
              </tr>

              <!-- Total Row -->
              <tr v-if="filteredTransactions.length > 0" class="bg-gray-50 font-semibold">
                <td colspan="4" class="px-4 py-3 text-right">Total:</td>
                <td class="px-4 py-3 whitespace-nowrap">{{ totalAmount }}</td>
                <td colspan="6"></td>
              </tr>

              <tr v-if="paginatedTransactions.length === 0">
                <td colspan="11" class="px-4 py-6 text-center text-gray-500">
                  No transactions found matching your criteria.
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        <div
          class="flex flex-col md:flex-row items-center justify-between mt-6 px-2 space-y-4 md:space-y-0"
        >
          <div class="text-sm text-gray-700">
            Showing {{ (currentPage - 1) * itemsPerPage + 1 }} to
            {{ Math.min(currentPage * itemsPerPage, filteredTransactions.length) }} of
            {{ filteredTransactions.length }} results
          </div>

          <div class="flex flex-col md:flex-row space-y-2 md:space-y-0 md:space-x-2">
            <select
              v-model="itemsPerPage"
              class="px-3 py-1.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-green-500"
            >
              <option :value="5">5 per page</option>
              <option :value="10">10 per page</option>
              <option :value="20">20 per page</option>
              <option :value="50">50 per page</option>
            </select>

            <div class="flex space-x-2">
              <button
                @click="currentPage--"
                :disabled="currentPage === 1"
                class="px-3 py-1.5 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors"
              >
                Previous
              </button>

              <div class="flex space-x-1 overflow-x-auto">
                <button
                  v-for="page in totalPages"
                  :key="page"
                  @click="currentPage = page"
                  :class="[
                    'px-3 py-1.5 border rounded-md transition-colors min-w-[2.5rem]',
                    currentPage === page
                      ? 'bg-green-600 text-white border-green-600'
                      : 'border-gray-300 hover:bg-gray-100',
                  ]"
                >
                  {{ page }}
                </button>
              </div>

              <button
                @click="currentPage++"
                :disabled="currentPage === totalPages"
                class="px-3 py-1.5 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Form Modal -->
      <TransactionFormModal
        v-if="showFormModal"
        :transaction="selectedTransaction"
        @close="showFormModal = false"
        @save="handleSave"
      />

      <!-- Delete Modal -->
      <ConfirmDeleteModal
        v-if="showDeleteModal"
        :item-name="selectedTransaction?.buyer_name || 'this transaction'"
        @close="showDeleteModal = false"
        @confirm="handleDelete"
      />
    </div>
  </div>
</template>

<style scoped>
/* Use a clean sans-serif font stack */
.font-sans {
  font-family:
    -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
}

/* Additional styling for better readability */
table {
  font-family:
    -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
}

th {
  font-weight: 600;
}

td {
  font-weight: 400;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .overflow-x-auto {
    -webkit-overflow-scrolling: touch;
  }
}
</style>
